// Cuenta corriente del cliente en la ficha (pedido de Rodrigo, 2026-11-01: «ver el detalle de la cuenta
// corriente de los clientes y poder pre visualizar la factura en pdf igual que en el b2b»). Plegada como
// en la app /ventas del B2B: lo que debe documento por documento y, aparte, los movimientos recientes.
// El número de cada factura del ERP abre su PDF; lo heredado de Random al corte no tiene PDF.
// Cobro en efectivo (2026-11-01): «Registrar pago en efectivo» deja el comprobante y el pago en curso.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { nombreDte } from '../dte/pdfDte';
import { useSesion } from '../sesion/SesionContexto';
import type { VistaRecepcion } from '../tesoreria/comprobanteRuta';
import type { ClienteRuta, Paquete } from './almacen';
import { CobroEfectivo, disponibleDe } from './CobroEfectivo';
import { useCola } from './cola';
import { pesos } from './logica';
import { VisorComprobante } from './VisorComprobante';
import { VisorPdf, type DocumentoPdf } from './VisorPdf';

export interface DocumentoCuenta {
  documentoId: string | null;
  /** Con qué se abona en un cobro en ruta (respuestas viejas no lo traen). */
  cxcId?: string;
  /** Pagado en efectivo a un vendedor y aún sin ingresar a tesorería. */
  enCurso?: string;
  heredado: boolean;
  tipoDte: number;
  folio: string | null;
  fechaEmision: string;
  fechaVencimiento: string;
  montoTotal: string;
  saldo: string;
  diasVencido: number;
  nota: string | null;
}

export interface MovimientoCuenta {
  fecha: string;
  tipo: 'CARGO' | 'NOTA_CREDITO' | 'PAGO';
  td: string;
  numero: string;
  /** «Heredado al corte · FCV N° 120 · vence …» (lo heredado dice aquí qué documento es). */
  descripcion: string;
  /** «Factura N° 5123», «Pago efectivo». */
  detalle: string;
  documentoId: string | null;
  cargo: string;
  abono: string;
  saldo: string;
}

export interface CuentaDelCliente {
  documentos: DocumentoCuenta[];
  saldoAFavor: string;
  pagosEnCurso?: Array<{
    recepcionId: string;
    numero: string;
    total: string;
    recibidoEn: string;
    vendedor: string;
  }>;
  desde: string;
  saldoInicial: string;
  movimientos: MovimientoCuenta[];
}

const fecha = (iso: string | null) =>
  iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(2, 4)}` : '—';

/** El PDF de un documento del cliente, por la API del vendedor (solo los de su cartera). */
export function pdfDelCliente(
  empresaId: string,
  clienteId: string,
  documentoId: string,
  titulo: string,
): DocumentoPdf {
  return {
    ruta: `/vendedor/clientes/${clienteId}/documentos/${documentoId}/pdf`,
    empresaId,
    titulo,
    // «Factura N° 5123» → MADECI-Factura-5123.pdf
    archivo: `MADECI-${titulo.replace(/N°\s*/g, '').trim().replace(/\s+/g, '-')}.pdf`,
  };
}

export function CuentaCorriente({
  paquete,
  cliente: c,
}: {
  paquete: Paquete;
  cliente: ClienteRuta;
}) {
  const s = useSesion();
  const cola = useCola();
  const enLinea = s.estado === 'autenticado' && cola.enLinea;
  const qc = useQueryClient();
  const [pdf, setPdf] = useState<DocumentoPdf | null>(null);
  const [cobrar, setCobrar] = useState(false);
  const [comprobante, setComprobante] = useState<VistaRecepcion | null>(null);
  const puedeCobrar = s.puede('cobranza.ruta.recibir');
  const q = useQuery({
    queryKey: ['vendedor', 'cuenta', paquete.empresaId, c.entidadId],
    queryFn: () =>
      api.get<CuentaDelCliente>(`/vendedor/clientes/${c.entidadId}/cuenta`, {
        query: {
          empresaId: paquete.empresaId,
          ...(paquete.verComo ? { vendedorId: paquete.verComo } : {}),
        },
      }),
    enabled: enLinea,
    staleTime: 60_000,
  });
  const cta = q.data;
  const abrir = (documentoId: string, titulo: string) =>
    setPdf(pdfDelCliente(paquete.empresaId, c.entidadId, documentoId, titulo));

  const resumen = cta
    ? cta.documentos.length
      ? `Cuenta corriente · ${cta.documentos.length} documento(s) pendiente(s)`
      : 'Cuenta corriente · sin documentos pendientes'
    : `Cuenta corriente${!enLinea ? ' · sin señal' : q.isError ? '' : ' · cargando…'}`;
  const totalPendiente = cta?.documentos.reduce((t, d) => t + Number(d.saldo), 0) ?? 0;
  const aFavor = Number(cta?.saldoAFavor ?? 0);
  const enCurso = cta?.pagosEnCurso ?? [];
  const totalEnCurso = enCurso.reduce((t, x) => t + Number(x.total), 0);
  const cobrables = cta?.documentos.filter((d) => d.cxcId && disponibleDe(d) > 0) ?? [];

  return (
    <>
      <details className="cta">
        <summary>{resumen}</summary>
        {!cta ? (
          q.isError ? (
            <div className="error">{(q.error as Error).message}</div>
          ) : !enLinea ? (
            <div className="aviso" style={{ marginTop: 10 }}>
              Para ver el detalle de la cuenta corriente necesitas señal.
            </div>
          ) : (
            <div className="vacio">Cargando la cuenta corriente…</div>
          )
        ) : cta.documentos.length ? (
          <div className="tabla-scroll">
            <table className="docs">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Emisión</th>
                  <th>Vence</th>
                  <th style={{ textAlign: 'right' }}>Saldo</th>
                </tr>
              </thead>
              <tbody>
                {cta.documentos.map((d, i) => {
                  const nombre = `${nombreDte(d.tipoDte)} ${d.folio ?? ''}`.trim();
                  return (
                    <tr key={d.documentoId ?? `h-${i}`} className={d.diasVencido > 0 ? 'venc' : ''}>
                      <td>
                        {d.documentoId ? (
                          <button
                            type="button"
                            className="link doc"
                            title="Ver el PDF"
                            onClick={() =>
                              abrir(
                                d.documentoId!,
                                `${nombreDte(d.tipoDte)} N° ${d.folio ?? ''}`.trim(),
                              )
                            }
                          >
                            {nombre}
                          </button>
                        ) : (
                          nombre
                        )}
                        {d.diasVencido > 0 ? ' ⚠' : ''}
                        {d.heredado && <small className="nota-doc">de Random, sin PDF</small>}
                      </td>
                      <td>{fecha(d.fechaEmision)}</td>
                      <td>
                        {fecha(d.fechaVencimiento)}
                        {d.diasVencido > 0 && (
                          <small className="nota-doc">hace {d.diasVencido} día(s)</small>
                        )}
                      </td>
                      <td className="n">
                        {pesos(Number(d.saldo))}
                        {d.saldo !== d.montoTotal && (
                          <small className="nota-doc">de {pesos(Number(d.montoTotal))}</small>
                        )}
                        {Number(d.enCurso ?? 0) > 0 && (
                          <small className="nota-doc en-curso">
                            en curso {pesos(Number(d.enCurso))}
                          </small>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {aFavor > 0 && (
                  <tr>
                    <td colSpan={3}>Saldo a favor del cliente</td>
                    <td className="n">−{pesos(aFavor)}</td>
                  </tr>
                )}
                <tr className="tot">
                  <td colSpan={3}>Total pendiente</td>
                  <td className="n">{pesos(totalPendiente - aFavor)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ok" style={{ marginTop: 10 }}>
            Este cliente está al día: no tiene documentos pendientes.
          </div>
        )}
        {enCurso.length > 0 && (
          <div className="en-curso-lista">
            <b>Pagos en curso · {pesos(totalEnCurso)}</b> (por ingresar a tesorería)
            {enCurso.map((x) => (
              <div key={x.recepcionId}>
                {x.numero} · {pesos(Number(x.total))} · {x.vendedor} ·{' '}
                {new Date(x.recibidoEn).toLocaleString('es-CL', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </div>
            ))}
          </div>
        )}
      </details>
      {cta && puedeCobrar && cobrables.length > 0 && (
        <button
          type="button"
          className="btn verde block"
          style={{ marginTop: 12 }}
          onClick={() => setCobrar(true)}
        >
          Registrar pago en efectivo
        </button>
      )}
      {cta && (
        <details className="cta">
          <summary>
            Movimientos desde el {fecha(cta.desde)} ({cta.movimientos.length})
          </summary>
          {cta.movimientos.length ? (
            <div className="tabla-scroll">
              <table className="docs">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Movimiento</th>
                    <th style={{ textAlign: 'right' }}>Monto</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {cta.movimientos.map((m, i) => {
                    const abono = Number(m.abono);
                    return (
                      <tr key={i}>
                        <td>{fecha(m.fecha)}</td>
                        <td>
                          {m.documentoId ? (
                            <button
                              type="button"
                              className="link doc"
                              title="Ver el PDF"
                              onClick={() => abrir(m.documentoId!, m.detalle)}
                            >
                              {m.detalle}
                            </button>
                          ) : m.tipo === 'PAGO' ? (
                            m.detalle
                          ) : (
                            m.descripcion
                          )}
                        </td>
                        <td className={`n${abono > 0 ? ' abono' : ''}`}>
                          {abono > 0 ? `−${pesos(abono)}` : pesos(Number(m.cargo))}
                        </td>
                        <td className="n">{pesos(Number(m.saldo))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="vacio">Sin movimientos en los últimos 90 días.</div>
          )}
        </details>
      )}
      {pdf && <VisorPdf doc={pdf} alCerrar={() => setPdf(null)} />}
      {cobrar && cta && (
        <CobroEfectivo
          paquete={paquete}
          cliente={c}
          documentos={cta.documentos}
          alCerrar={() => setCobrar(false)}
          alRegistrar={(v) => {
            setCobrar(false);
            setComprobante(v);
            void qc.invalidateQueries({
              queryKey: ['vendedor', 'cuenta', paquete.empresaId, c.entidadId],
            });
            void qc.invalidateQueries({ queryKey: ['vendedor', 'cobros'] });
          }}
        />
      )}
      {comprobante && <VisorComprobante v={comprobante} alCerrar={() => setComprobante(null)} />}
    </>
  );
}
